import React, { useEffect, useState } from "react";
import { ArrowRight, Zap as Lightning, TrendingUp as ChartLineUp, Brain, Layers as Stack, ShieldCheck, Sparkles, Rocket, Plug, Calendar, Mic, Image, Globe, RefreshCw, BarChart3, Users, Crown, Flame, Star, BookOpen, MessageCircle, Mail, DollarSign, Library, Radio } from "lucide-react";
import { motion } from "framer-motion";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function login() {
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

const PILLARS = [
  { icon: BookOpen, label: "Strategy AI", title: "Substack-native, not generic", desc: "Every post is written against the live Substack playbook — Publication Architecture, Growth, Community, the Evergreen List Ecosystem loop, and Monetization. Smaller brands get a lighter context; mature brands get the full strategy stack injected automatically." },
  { icon: Brain, label: "Brain", title: "You = creative force", desc: "Run iterative 20-question AI interviews. Upload anything — PDFs, docs, images, audio, video. Every input deepens the brand's knowledge graph." },
  { icon: Sparkles, label: "Engine", title: "One click = year of viral content", desc: "Substack is the engine. Press one button: 52 weeks of posts written using proven viral playbooks (Hormozi, Welsh, Naval, contrarian, story→offer) — plus a 6-post launch sequence for brand-new publications." },
  { icon: Globe, label: "Multicast", title: "Adapts everywhere automatically", desc: "Every Substack post is auto-adapted for Instagram, X, LinkedIn, TikTok, YouTube, Pinterest, Threads, Facebook, Reddit, Medium — native format, native voice, all driving back to your Substack via one Substack-first CTA line." },
  { icon: RefreshCw, label: "Self-improving", title: "Pending content gets sharper as you teach it", desc: "Add new context — an interview answer, an uploaded transcript, an accepted knowledge flag — and every unlaunched post is marked stale. One click refreshes the entire pending queue with the new context woven in." },
  { icon: Rocket, label: "Autopilot + Guardrails", title: "Approve once. Walk away.", desc: "Toggle autopilot per brand. Substack drafts auto-create with 1-click publish; 9 social platforms ship via Ayrshare. Approval drawer with live Substack preview, A/B headlines, AI-art-directed covers (Nano Banana), inline edit, reject-with-notes → instant rewrite. Nothing publishes without you." },
];

const PLATFORMS = [
  { name: "Substack", c: "#FF6719" },
  { name: "Substack Notes", c: "#FF6719" },
  { name: "Instagram", c: "#E1306C" },
  { name: "X", c: "#000000" },
  { name: "LinkedIn", c: "#0A66C2" },
  { name: "YouTube", c: "#FF0000" },
  { name: "TikTok", c: "#000000" },
  { name: "Threads", c: "#000000" },
  { name: "Pinterest", c: "#E60023" },
  { name: "Facebook", c: "#1877F2" },
  { name: "Reddit", c: "#FF4500" },
  { name: "Medium", c: "#000000" },
  { name: "Spotify+Apple Pods", c: "#1DB954" },
];

const STRATEGY_MODULES = [
  { icon: BookOpen, label: "Foundation", title: "Calm, civil register · paid-subscriber economics", desc: "Anti-hype voice rules. Pricing intuition ($5/$50/$497 mental anchor; single-digit beats double-digit). Owner-builder vs creator-economy framing. Every post inherits these defaults." },
  { icon: Library, label: "Publication Architecture", title: "Movement / Trojan-horse / Course / Backstage modes", desc: "Multi-publication setup with sections, podcast attachment, copy-existing-list rules. Posts auto-route to the right publication + section so a single workspace can run a fund + a paid course + a backstage Substack without leakage." },
  { icon: ChartLineUp, label: "Growth", title: "Notes are the #1 organic lever", desc: "Notes-first cadence per brand (2/day default). Posting dayparts. Category-aware monetizability tier (high/mid/low) tunes how aggressively the writer leans paid. Restack & comment recommendations the human acts on." },
  { icon: MessageCircle, label: "Community", title: "Livestream = podcast. Comments = community.", desc: "When the brand has community levers (livestream cadence, comment threads, chat nudges, short video), 52 weeks of community touchpoints are auto-scheduled into the calendar as human-action items — never auto-publishes." },
  { icon: Mail, label: "Evergreen Loop", title: "Substack list IS the engine", desc: "Every adapted platform CTA points back to Substack. The published list is the asset off-platform offers (courses, coaching, sponsorships) get sold to. The writer treats the list as the revenue spine, not the byproduct." },
  { icon: DollarSign, label: "Monetization", title: "Tier · gating · email_or_not (publish ≠ email)", desc: "Founder/VIP tier ($200-$1,888+/yr, non-renewing), gated classroom (Next/Previous nav), paid podcast with free teasers, custom join links, external offers, sponsorship rate cards — when enabled, the writer adapts every CTA, every gating decision, and every email-blast slot to fit." },
];

const STACK_ITEMS = [
  { icon: BookOpen, k: "6 Substack strategy docs · live & editable", v: "Foundation, Publication Architecture, Growth, Community, Evergreen Loop, Monetization. Owner can edit the playbook from the Strategy page; every future generation picks up the change." },
  { icon: Rocket, k: "6-post launch sequence for new publications", v: "Manifesto → Origin → Promise → Free Anchor → Paid Preview → Invitation. Auto-detected for day-1 brands and woven into the year-of-content plan." },
  { icon: Radio, k: "4-week rolling Notes plan + restacks", v: "Year-gen ships ~60 calm, story-first Substack Notes (no hashtags/emojis/hype) + 8 restack actions for the human to do. Refreshes monthly." },
  { icon: Calendar, k: "52-week community touchpoint calendar", v: "Livestream slots, comment-thread engagement reminders, chat-as-nudge prompts, short-video Note slots — all human-action items, never auto-published. Idempotent: re-running year-gen won't duplicate." },
  { icon: Mail, k: "Publish ≠ email · per-post gating", v: "Writer outputs `type / gating / email_or_not / schedule_slot` per post. Free anchor blasts; paid pieces use a measured email cadence; founders ship only on the configured rhythm. Soft-paywall override for paid_preview." },
  { icon: DollarSign, k: "Monetization-aware Substack-first CTA", v: "When custom_join_link is set, every one of the 12 adapted platforms ends with that exact destination. Substack is the engine; everything else is a billboard pointing back." },
  { icon: Library, k: "Multi-publication routing", v: "Brands run multiple publications + sections under one workspace. Posts auto-route to the right section (Field Notes, Paid Tier, Backstage). Day-2 architecture is live in the writer." },
  { icon: Brain, k: "Iterative 20-Q AI Interview", v: "Each question builds on the last. Claude Sonnet 4.5 + your accepted knowledge flags." },
  { icon: Stack, k: "Multi-Brand + Cross-Pollination", v: "Own brands + client brands marked distinctly. Opt-in cross-pollination per pair with notes." },
  { icon: Mic, k: "Universal upload + video repurpose", v: "Drop a video — we extract audio (ffmpeg), transcribe (Whisper), and turn the transcript into a Substack draft. Auto." },
  { icon: Image, k: "AI-art-directed cover images", v: "Claude writes a viral-tuned art brief (audience-aware, pattern-interrupt, cinematic). Nano Banana renders it. 6 style presets." },
  { icon: Sparkles, k: "Viral playbook library", v: "7 curated frameworks (Hormozi Grand Slam, Welsh listicle, contrarian, Naval, case-study). Writer rotates them per post." },
  { icon: Calendar, k: "Year-of-content + DnD calendar", v: "Generate a year in one click. Drag posts to reschedule. Color-coded by status & brand. Pipeline tabs (Pending / Approved / Published) with deep-linking from the calendar." },
  { icon: BarChart3, k: "Per-post analytics → self-tuning writer", v: "Drop in open/click/replies/shares. Top performers feed back into the writer prompt as 'mirror what worked'." },
  { icon: Users, k: "Multi-user workspaces", v: "Invite collaborators per workspace. Brands & content shared across members. Workspace-isolated access." },
  { icon: Plug, k: "Auto-publish via Ayrshare wizard", v: "5-step in-app wizard guides you through sign-up + payment + connecting every platform. Bills directly with Ayrshare." },
  { icon: Rocket, k: "Autopilot scheduler", v: "Once approved, posts ship to every connected platform at the scheduled date with zero clicks. Substack drafts auto-create via your authenticated session; 1 click publishes." },
];

export default function Landing() {
  const [wall, setWall] = useState({ founders: [], filled: 0, remaining: 50, slots: 50 });
  const [spotlight, setSpotlight] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/billing/founders-wall`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && setWall(d))
      .catch(() => {});
    fetch(`${BACKEND_URL}/api/mogul/spotlight`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => d && d.spotlight && setSpotlight(d.spotlight))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-white text-[#0A0A0B]">
      <header className="border-b border-[#E2E4E9] sticky top-0 bg-white z-30">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[#0A0A0B]" />
            <span className="font-display font-black tracking-tighter text-lg">VITALITY/<span className="text-[#002FA7]">SS</span></span>
          </div>
          <div className="flex items-center gap-3 text-xs mono uppercase text-[#52525B]">
            <a href="#how" className="hidden md:inline hover:text-[#0A0A0B]">How</a>
            <a href="#strategy" className="hidden md:inline hover:text-[#0A0A0B]">Strategy</a>
            <a href="#stack" className="hidden md:inline hover:text-[#0A0A0B]">Stack</a>
            <a href="#platforms" className="hidden md:inline hover:text-[#0A0A0B]">Platforms</a>
            <a href="/operators" className="hidden md:inline hover:text-[#0A0A0B]" data-testid="nav-operators">Operators</a>
            <a href="/pricing" className="hidden md:inline hover:text-[#0A0A0B]" data-testid="nav-pricing">Pricing</a>
            <button
              onClick={login}
              data-testid="login-button"
              className="bg-[#0A0A0B] text-white px-4 py-2 text-sm hover:bg-[#002FA7] transition-colors normal-case tracking-normal"
            >
              Sign in with Google
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative border-b border-[#E2E4E9] overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-30 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-24 md:py-32 grid md:grid-cols-12 gap-12 items-end">
          <div className="md:col-span-8">
            <div className="text-eyebrow mb-6">Substack strategy AI · 12-platform multicast · Autopilot</div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="font-display font-black tracking-tighter text-5xl md:text-7xl lg:text-[5.5rem] leading-[0.9]"
            >
              A year of viral content.<br />
              Across <span className="text-[#002FA7]">every platform.</span><br />
              Zero touch after you approve.
            </motion.h1>
            <p className="mt-8 text-lg text-[#52525B] max-w-2xl leading-relaxed">
              You do the interviews and upload the raw material. <strong className="text-[#0A0A0B]">We write the year</strong> against
              a live Substack strategy playbook (Foundation, Publication Architecture, Growth, Community, Evergreen Loop, Monetization) —
              then adapt every Substack post into native versions for Instagram, X, LinkedIn, TikTok, YouTube, Pinterest, Threads,
              Facebook, Reddit, Medium, and podcasts. Approve once. Walk away.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={login}
                data-testid="hero-cta-button"
                className="bg-[#0A0A0B] text-white px-6 py-4 text-sm tracking-wide flex items-center gap-2 hover:bg-[#002FA7] transition-colors group"
              >
                Start Free · Sign in with Google
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="#how" className="px-6 py-4 text-sm tracking-wide border border-[#0A0A0B] hover:bg-[#F4F5F6]">
                See how it works
              </a>
            </div>
            <p className="mt-6 text-xs mono uppercase text-[#A1A1AA]">
              Substack auto-drafts via your authenticated session — 1-click publish · 9 social platforms auto-publish via Ayrshare ($149-349/mo, in-app wizard) · Stripe + Connect for paid tier + affiliate payouts
            </p>
          </div>
          <div className="md:col-span-4 hidden md:block">
            <div className="border border-[#0A0A0B] bg-white p-6 brutal-shadow-lg">
              <div className="text-eyebrow mb-3">A real brand snapshot</div>
              <div className="space-y-3 mono text-sm">
                <Row k="Knowledge score" v="84 / 100" />
                <Row k="Year queue" v="52 posts" />
                <Row k="Auto-adapted versions" v="468" sub="(52 × 9 platforms)" />
                <Row k="Substack Notes (rolling)" v="60 / 4wk" />
                <Row k="Community touchpoints" v="52 / yr" />
                <Row k="Strategy docs injected" v="5 / 6" sub="(per brand config)" />
                <Row k="Pending approvals" v="4" />
                <Row k="Avg viral score" v="86" pos />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SIX PILLARS */}
      <section id="how" className="border-b border-[#E2E4E9]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-eyebrow mb-4">The system</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-12 max-w-3xl">
            You're the brain. The app is the output engine.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#E2E4E9]">
            {PILLARS.map((p, i) => (
              <div key={i} className="p-8 border-r border-b border-[#E2E4E9] hover:bg-[#F4F5F6] transition-colors">
                <div className="text-eyebrow mb-2">{p.label}</div>
                <p.icon size={28} />
                <div className="font-display font-bold text-xl mt-4 tracking-tight">{p.title}</div>
                <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{p.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SUBSTACK STRATEGY MODULES */}
      <section id="strategy" className="border-b border-[#E2E4E9]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-eyebrow mb-4 flex items-center gap-2"><BookOpen size={14}/> The differentiator · 6 strategy modules · injected per brand</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-4 max-w-3xl">
            Substack-native strategy.<br />
            <span className="text-[#002FA7]">Not generic AI writing.</span>
          </h2>
          <p className="text-[#52525B] max-w-2xl mb-12 leading-relaxed">
            Most AI writers will produce 52 posts. Ours produces 52 <em>Substack</em> posts — every prompt sees the brand's
            Foundation, Publication Architecture, Growth, Community, Evergreen Loop, and Monetization knowledge.
            Smaller brands get the lean default; mature publications get the full strategy stack injected automatically.
            <strong className="text-[#0A0A0B]"> No tokens wasted, no rules ignored.</strong>
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-0 border-l border-t border-[#E2E4E9]">
            {STRATEGY_MODULES.map((m, i) => (
              <div key={i} className="p-8 border-r border-b border-[#E2E4E9] hover:bg-[#FFFBEB] transition-colors" data-testid={`strategy-module-${i}`}>
                <div className="text-eyebrow mb-2 flex items-center gap-2">
                  <m.icon size={14}/>
                  {m.label}
                </div>
                <div className="font-display font-bold text-xl tracking-tight mt-2">{m.title}</div>
                <p className="text-sm text-[#52525B] mt-2 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
          <p className="text-xs mono uppercase text-[#A1A1AA] mt-6">
            All 6 docs are seeded on day one and editable in-app from the Strategy page — your tweaks flow into the next generation.
          </p>
        </div>
      </section>

      {/* PLATFORMS */}
      <section id="platforms" className="border-b border-[#E2E4E9] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-eyebrow mb-4">12 platforms · 1 source of truth</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-4 max-w-3xl">
            Substack stays the brain.<br />
            Everything else amplifies it.
          </h2>
          <p className="text-[#52525B] max-w-2xl mb-12">
            Each platform gets a native adaptation — proper character limits, hook conventions, hashtag rules, thread/carousel/video script structure — and a CTA pointing readers back to your Substack publication.
          </p>
          <div className="flex flex-wrap gap-2">
            {PLATFORMS.map((p) => (
              <div key={p.name} className="flex items-center gap-2 border border-[#E2E4E9] bg-white px-3 py-2 text-sm">
                <span className="inline-block w-3 h-3" style={{ background: p.c }} />
                {p.name}
              </div>
            ))}
          </div>
          <p className="text-xs mono uppercase text-[#A1A1AA] mt-6">
            Quora and Tumblr intentionally dropped (low ROI). Substack Notes & Medium stay copy-paste (no public API exists).
          </p>
        </div>
      </section>

      {/* STACK */}
      <section id="stack" className="border-b border-[#E2E4E9]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-eyebrow mb-4">Under the hood</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-12 max-w-3xl">
            Every line engineered for viral sales.
          </h2>
          <div className="grid md:grid-cols-2 gap-0 border-l border-t border-[#E2E4E9]">
            {STACK_ITEMS.map((s, i) => (
              <div key={i} className="p-6 border-r border-b border-[#E2E4E9]">
                <div className="flex items-center gap-3 mb-2">
                  <s.icon size={20} />
                  <div className="font-display font-bold text-lg tracking-tight">{s.k}</div>
                </div>
                <p className="text-sm text-[#52525B] leading-relaxed">{s.v}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WORKFLOW */}
      <section className="border-b border-[#E2E4E9] bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="text-eyebrow mb-4">A typical week</div>
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl mb-12 max-w-3xl">
            What you actually do.
          </h2>
          <div className="space-y-4 max-w-4xl">
            <Step n="1" t="Add a brand" d="Mark it client or your own. Set what it sells, the voice, the primary category, and your posting cadence." />
            <Step n="2" t="Configure publications + monetization (optional but powerful)" d="Spin up multiple publications + sections under one workspace. Toggle founder/VIP tier, gated classroom, paid podcast, custom join links, external offers, sponsorship rate card. The writer adapts to whatever you turn on." />
            <Step n="3" t="Run an interview (20 questions, ~10 min)" d="The AI asks. You answer. Each question builds on the last." />
            <Step n="4" t="Drop anything you have" d="PDFs, docs, transcripts, photos, audio clips, raw videos. We auto-detect kind, extract text, transcribe audio, vision-analyze images, and turn videos into Substack drafts in one click." />
            <Step n="5" t="Enable your platforms (Platforms tab)" d="Click into the brand → Platforms → enable IG, X, LinkedIn, etc. Run the 5-step in-app Ayrshare wizard to wire auto-posting." />
            <Step n="6" t="Click 'Generate a year of viral content'" d="One button → 52 weekly Substack posts (with a 6-post launch sequence if you're day one) + a 4-week rolling Notes plan + 52 weeks of community touchpoints + automatic native adaptation for every enabled platform." />
            <Step n="7" t="Review the queue, approve" d="Live Substack preview, A/B headline variants, gating + email_or_not + schedule_slot badges per post, AI cover images you can regenerate or replace, inline edit, reject-with-notes → instant rewrite." />
            <Step n="8" t="Flip autopilot on" d="From the moment you approve, every post ships to every platform on its scheduled date. Substack drafts via your authenticated session (1 click); 9 social platforms via Ayrshare. You walk away." />
            <Step n="9" t="Keep feeding it new context" d="Whenever you upload anything new or accept a knowledge flag, all unlaunched content gets marked stale. One tap refreshes it with the new context — pending content gets sharper as you teach the brand." />
          </div>
        </div>
      </section>

      {/* SPOTLIGHT — rotating Mogul of the day */}
      {spotlight && (
        <section className="border-b border-[#E2E4E9]" data-testid="spotlight-section">
          <div className="max-w-7xl mx-auto px-6 py-20">
            <div className="text-eyebrow mb-4 flex items-center gap-2"><Star size={14} className="text-[#F59E0B]"/> Operator spotlight · rotates every 24h</div>
            <div className="grid md:grid-cols-12 gap-8 items-center border-2 border-black bg-white p-8 brutal-shadow-lg">
              {spotlight.headshot_url && (
                <div className="md:col-span-3">
                  <img src={spotlight.headshot_url} alt={spotlight.agency_name} className="w-full aspect-square object-cover border-2 border-black"/>
                </div>
              )}
              <div className={spotlight.headshot_url ? "md:col-span-6" : "md:col-span-9"}>
                <div className="font-display font-black text-3xl tracking-tighter mb-2" style={{ color: spotlight.accent_color || "#0A0A0B" }}>{spotlight.agency_name}</div>
                {spotlight.tagline && <p className="text-lg text-[#52525B] leading-relaxed">"{spotlight.tagline}"</p>}
                {spotlight.services_preview?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-4">
                    {spotlight.services_preview.map((s, i) => (
                      <span key={i} className="text-xs mono px-2 py-1 border border-[#E2E4E9]">{s}</span>
                    ))}
                  </div>
                )}
              </div>
              <div className="md:col-span-3 flex flex-col gap-2">
                <a href={`/m/${spotlight.slug}`} className="bg-black text-white px-5 py-3 text-sm flex items-center justify-center gap-2 hover:bg-[#002FA7] transition-colors" data-testid="spotlight-visit-button">
                  Visit profile <ArrowRight size={14}/>
                </a>
                <a href="/pricing" className="border-2 border-black px-5 py-3 text-sm text-center hover:bg-black hover:text-white transition-colors text-xs mono uppercase" data-testid="spotlight-become-button">
                  Want this for yourself? →
                </a>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FOUNDERS WALL */}
      <section className="border-b border-[#E2E4E9]" data-testid="founders-wall-section">
        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="flex items-end justify-between flex-wrap gap-6 mb-10">
            <div>
              <div className="text-eyebrow mb-4 flex items-center gap-2"><Crown size={14}/> Founders Wall</div>
              <h2 className="font-display font-black tracking-tighter text-4xl md:text-5xl max-w-3xl">
                {wall.remaining > 0 ? "The first 50 who locked in." : "The 50 are in."}
              </h2>
              <p className="text-[#52525B] mt-3 max-w-2xl">
                Founding Moguls get Vitality forever for one payment — and a permanent place on this wall.
              </p>
            </div>
            <div className="border-2 border-black bg-white p-5 brutal-shadow-lg">
              <div className="text-eyebrow mb-1">Slots remaining</div>
              <div className="font-display font-black text-5xl tracking-tighter">
                <span className={wall.remaining < 10 ? "text-[#E11D48]" : ""}>{wall.remaining}</span>
                <span className="text-[#A1A1AA] text-2xl"> / {wall.slots}</span>
              </div>
              {wall.remaining < 10 && wall.remaining > 0 && (
                <div className="mt-2 flex items-center gap-1 text-xs mono text-[#E11D48]"><Flame size={12}/> Closing fast</div>
              )}
            </div>
          </div>

          {/* Always render the 50-cell grid — empty cells visualize remaining slots */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 lg:grid-cols-10 gap-3">
            {Array.from({ length: wall.slots }).map((_, i) => {
              const founder = wall.founders[i];
              if (founder) {
                return (
                  <div key={i} className="border border-black bg-white p-3 flex flex-col items-center text-center group hover:bg-[#FFFBEB] transition-colors" data-testid={`founder-slot-${i}`}>
                    {founder.picture ? (
                      <img src={founder.picture} alt="" className="w-12 h-12 mb-2 border border-black object-cover"/>
                    ) : (
                      <div className="w-12 h-12 mb-2 border border-black bg-[#F4F5F6] flex items-center justify-center font-display font-bold text-xl">
                        {(founder.name || "?")[0]?.toUpperCase()}
                      </div>
                    )}
                    <div className="text-xs font-semibold truncate w-full">{founder.name}</div>
                    <div className="text-[10px] mono text-[#A1A1AA] mt-1">#{i + 1}</div>
                  </div>
                );
              }
              return (
                <a key={i} href="/pricing" className="border border-dashed border-[#E2E4E9] p-3 flex flex-col items-center text-center hover:border-black hover:bg-[#FFFBEB] transition-colors" data-testid={`founder-empty-slot-${i}`}>
                  <div className="w-12 h-12 mb-2 border border-dashed border-[#A1A1AA] flex items-center justify-center text-[#A1A1AA]">
                    <Crown size={14}/>
                  </div>
                  <div className="text-[10px] mono text-[#A1A1AA] mt-1">#{i + 1} · open</div>
                </a>
              );
            })}
          </div>

          {wall.founders.length === 0 && (
            <div className="mt-6 text-center">
              <a href="/pricing" className="inline-flex items-center gap-2 bg-black text-white px-5 py-3 text-sm hover:bg-[#F59E0B] hover:text-black transition-colors" data-testid="founders-cta-empty">
                Claim slot #1 → $2,997 once · forever <ArrowRight size={14}/>
              </a>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#0A0A0B] text-white">
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <h2 className="font-display font-black tracking-tighter text-4xl md:text-6xl leading-none">
            Stop managing the platforms.<br />
            Start <span className="text-[#F59E0B]">selling everywhere.</span>
          </h2>
          <div>
            <p className="text-base text-white/70 mb-8 max-w-md">
              You're 4 seconds from sign-in. 90 seconds from your first viral-engineered draft.
              A few hours from a year of cross-platform content scheduled and ready to ship.
            </p>
            <button
              onClick={login}
              data-testid="footer-cta-button"
              className="bg-white text-black px-6 py-4 text-sm tracking-wide flex items-center gap-2 hover:bg-[#F59E0B] hover:text-black transition-colors"
            >
              Sign in with Google <ArrowRight size={16} />
            </button>
          </div>
        </div>
      </section>

      <footer className="border-t border-[#E2E4E9]">
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between text-xs text-[#A1A1AA]">
          <span>© Vitality/SS · 2026</span>
          <span className="mono">Built on proven viral frameworks · 6 live Substack strategy docs · Claude Sonnet 4.5 · Nano Banana · Whisper · Resend · Ayrshare · Stripe + Connect</span>
        </div>
      </footer>
    </div>
  );
}

function Row({ k, v, sub, pos }) {
  return (
    <div className="flex items-baseline justify-between border-b border-[#E2E4E9] pb-2">
      <span className="text-eyebrow">{k}</span>
      <div className="text-right">
        <span className={`text-base font-semibold ${pos ? "text-[#059669]" : ""}`}>{v}</span>
        {sub && <div className="text-[10px] mono text-[#A1A1AA]">{sub}</div>}
      </div>
    </div>
  );
}

function Step({ n, t, d }) {
  return (
    <div className="border border-[#E2E4E9] p-5 flex gap-5 bg-white hover:border-[#0A0A0B] transition-colors">
      <div className="font-display font-black text-4xl tracking-tighter text-[#A1A1AA] w-12 shrink-0">{n}</div>
      <div>
        <div className="font-display font-bold text-xl tracking-tight">{t}</div>
        <p className="text-sm text-[#52525B] mt-1 leading-relaxed">{d}</p>
      </div>
    </div>
  );
}
